import AsyncStorage from "@react-native-async-storage/async-storage";

/** Lokal pro Nutzer und Verein. Kein created_by, keine Abfrage der Fälle. */
export function lastCareSiteKey(userId: string, organizationId: string): string {
  return `ferina.intake.lastCareSite.${userId}.${organizationId}`;
}

export async function readLastCareSiteId(
  userId: string,
  organizationId: string,
): Promise<string | null> {
  try {
    const value = await AsyncStorage.getItem(lastCareSiteKey(userId, organizationId));
    const trimmed = value?.trim() ?? "";
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

export async function writeLastCareSiteId(
  userId: string,
  organizationId: string,
  careSiteId: string,
): Promise<void> {
  const id = careSiteId.trim();
  if (!userId || !organizationId || !id) return;
  try {
    await AsyncStorage.setItem(lastCareSiteKey(userId, organizationId), id);
  } catch {
    /* Der nächste Start belegt dann nur die einzelne Stelle vor. */
  }
}

/**
 * Finder-Hinweis für „Weiteres Tier“.
 * Sichtbar nur für dieselbe Nutzer-Id und nur, wenn die gespeicherten
 * caseIds dieselben Fälle sind, die rememberIntakeCases beim erfolgreichen
 * Speichern festgehalten hat. Keine Vereinsbindung; die letzte Pflegestelle
 * hängt getrennt an Nutzer und Verein.
 * handoff bleibt wahr, bis der neu hängende Screen ihn annimmt: das
 * Entfernen des Entwurfs hängt die Aufnahme neu ein, und genau dieses
 * Aushängen darf den Hinweis nicht löschen. Ein späteres Verlassen,
 * ein Nutzerwechsel, userId null oder ein frisches Formular löscht ihn.
 */
export type IntakeFollowup = {
  userId: string;
  caseIds: readonly string[];
  text: string;
  generation: number;
  handoff: boolean;
};

type TakenCases = {
  userId: string;
  caseIds: readonly string[];
};

let followup: IntakeFollowup | null = null;
let takenCases: TakenCases | null = null;
let followupGeneration = 0;
const followupListeners = new Set<() => void>();
let weightFocusPending = false;

function caseIdList(caseIds: readonly string[]): string[] {
  return caseIds.map((id) => id.trim()).filter((id) => id.length > 0);
}

/** Dieselben Fälle, Reihenfolge egal. Leer passt nie. */
export function sameIntakeCaseIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length === 0 || left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((id, index) => id === b[index]);
}

function emitFollowup(): void {
  for (const listener of followupListeners) listener();
}

export function subscribeIntakeFollowup(listener: () => void): () => void {
  followupListeners.add(listener);
  return () => {
    followupListeners.delete(listener);
  };
}

/** Fälle der gelungenen Aufnahme. Der Hinweis gilt nur für genau diese Ids. */
export function rememberIntakeCases(
  userId: string | null | undefined,
  caseIds: readonly string[],
): void {
  const ids = caseIdList(caseIds);
  if (!userId || ids.length === 0) {
    takenCases = null;
    return;
  }
  takenCases = { userId, caseIds: ids };
}

export function armIntakeFollowup(input: {
  userId: string | null;
  caseIds: readonly string[];
  text: string | null | undefined;
}): void {
  const text = input.text?.trim() ?? "";
  const caseIds = caseIdList(input.caseIds);
  if (!text || input.userId == null || caseIds.length === 0) {
    followup = null;
    emitFollowup();
    return;
  }
  followupGeneration += 1;
  followup = {
    userId: input.userId,
    caseIds,
    text,
    generation: followupGeneration,
    handoff: true,
  };
  emitFollowup();
}

function noticeMatches(userId: string | null | undefined): boolean {
  if (!followup || !takenCases || userId == null) return false;
  if (followup.userId !== userId || takenCases.userId !== userId) return false;
  return sameIntakeCaseIds(followup.caseIds, takenCases.caseIds);
}

export function readIntakeFollowupNotice(userId: string | null | undefined): string | null {
  if (!noticeMatches(userId) || !followup) return null;
  return followup.text;
}

export function readIntakeFollowupRecord(): IntakeFollowup | null {
  if (!followup) return null;
  return { ...followup, caseIds: [...followup.caseIds] };
}

/** Nimmt den Hinweis an, wenn er für diesen Nutzer und diese Fälle übergeben wird. */
export function acceptIntakeFollowup(userId: string | null | undefined): IntakeFollowup | null {
  if (!followup?.handoff || !noticeMatches(userId)) return null;
  followup = { ...followup, handoff: false };
  return { ...followup, caseIds: [...followup.caseIds] };
}

export function clearIntakeFollowupNotice(): void {
  if (!followup) return;
  followup = null;
  emitFollowup();
}

/** Abmeldung: Hinweis und die gerade aufgenommenen Fälle weg. */
export function clearIntakeFollowupSession(): void {
  if (!followup && !takenCases) return;
  followup = null;
  takenCases = null;
  emitFollowup();
}

/** Löscht nur, wenn dieser Screen den Hinweis schon angenommen hat und niemand einen neueren übergibt. */
export function clearIntakeFollowupIfOwned(ownedGeneration: number): void {
  if (!followup || followup.handoff) return;
  if (followup.generation !== ownedGeneration) return;
  followup = null;
  emitFollowup();
}

export function requestIntakeWeightFocus(): void {
  weightFocusPending = true;
}

export function consumeIntakeWeightFocus(): boolean {
  const pending = weightFocusPending;
  weightFocusPending = false;
  return pending;
}

export function resetIntakeFollowupForTests(): void {
  followup = null;
  takenCases = null;
  followupGeneration = 0;
  followupListeners.clear();
  weightFocusPending = false;
}
